import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Plus, Trash2, Save, Send, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE as API } from '../../lib/apiBase';
import { safeJson } from '../../lib/safeJson';
import {
  PROFILE_FIELDS, COURSE_COLS, BOOK_COLS, PAPER_COLS, FEEDBACK_COLS,
  GUARDIAN_FIELDS, TEXT_BLOCKS, ADMISSION_ROWS, emptyRow,
} from './faConstants';

const h = () => {
  const t = localStorage.getItem('hrms-token');
  return t ? { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
};

// Legacy forms stored a single object / single string — normalise both to lists
const asRows = (v, cols) => {
  if (Array.isArray(v)) return v.length ? v : [emptyRow(cols)];
  if (v && typeof v === 'object') return [v];
  return [emptyRow(cols)];
};
const asList = (v) => {
  if (Array.isArray(v)) return v.length ? v : [''];
  return v ? [v] : [''];
};

const SectionTitle = ({ children }) => (
  <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-800 dark:text-slate-100 border-l-4 border-[#DC2626] pl-2 mt-8 mb-3">
    {children}
  </h3>
);

function RowTable({ cols, rows, onChange, testid, readOnly }) {
  const list = rows && rows.length ? rows : [emptyRow(cols)];
  const set = (i, key, val) => onChange(list.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));
  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="overflow-x-auto rounded-md border border-slate-200 dark:border-slate-700">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 dark:bg-slate-800">
            <tr>
              <th className="p-2 text-left w-8">#</th>
              {cols.map(c => <th key={c.key} className="p-2 text-left font-medium">{c.label}</th>)}
              {!readOnly && <th className="w-10" />}
            </tr>
          </thead>
          <tbody>
            {list.map((r, i) => (
              <tr key={i} className="border-t border-slate-100 dark:border-slate-800">
                <td className="p-1 text-center text-slate-400">{i + 1}</td>
                {cols.map(c => (
                  <td key={c.key} className="p-1">
                    <Input
                      className="h-8 text-xs"
                      value={r[c.key] || ''}
                      disabled={readOnly}
                      onChange={e => set(i, c.key, e.target.value)}
                      data-testid={`${testid}-${i}-${c.key}`}
                    />
                  </td>
                ))}
                {!readOnly && (
                  <td className="p-1">
                    <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500"
                      onClick={() => onChange(list.filter((_, idx) => idx !== i))}
                      data-testid={`${testid}-del-${i}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!readOnly && (
        <Button type="button" variant="outline" size="sm" onClick={() => onChange([...list, emptyRow(cols)])}
          data-testid={`${testid}-add`}>
          <Plus className="w-3.5 h-3.5 mr-1" /> Add Row
        </Button>
      )}
    </div>
  );
}

// Narrative block that accepts as many separate entries as the faculty needs
function MultiText({ label, hint, value, onChange, readOnly, testid, rows = 3, placeholder }) {
  const list = asList(value);
  const set = (i, v) => onChange(list.map((x, idx) => (idx === i ? v : x)));
  return (
    <div className="space-y-2 mb-5" data-testid={testid}>
      <Label className="text-xs uppercase font-semibold">{label}</Label>
      {hint && <p className="text-xs text-slate-400">{hint}</p>}
      {list.map((v, i) => (
        <div key={i} className="flex gap-2 items-start">
          <span className="text-xs text-slate-400 pt-2 w-5 text-right shrink-0">{i + 1}.</span>
          <Textarea
            rows={rows}
            value={v || ''}
            disabled={readOnly}
            placeholder={placeholder}
            onChange={e => set(i, e.target.value)}
            data-testid={`${testid}-${i}`}
          />
          {!readOnly && list.length > 1 && (
            <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500 shrink-0"
              onClick={() => onChange(list.filter((_, idx) => idx !== i))}
              data-testid={`${testid}-del-${i}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      ))}
      {!readOnly && (
        <Button type="button" variant="outline" size="sm" className="ml-7"
          onClick={() => onChange([...list, ''])} data-testid={`${testid}-add`}>
          <Plus className="w-3.5 h-3.5 mr-1" /> Add another entry
        </Button>
      )}
    </div>
  );
}

// Repeatable single-line list (strengths / weaknesses)
function MultiLine({ label, value, onChange, readOnly, testid }) {
  const list = asList(value);
  const set = (i, v) => onChange(list.map((x, idx) => (idx === i ? v : x)));
  return (
    <div className="space-y-2" data-testid={testid}>
      <Label className="text-xs uppercase font-semibold">{label}</Label>
      {list.map((v, i) => (
        <div key={i} className="flex gap-2 items-center">
          <span className="text-xs text-slate-400 w-5 text-right shrink-0">{i + 1}.</span>
          <Input value={v || ''} disabled={readOnly} placeholder={`${label} ${i + 1}`}
            onChange={e => set(i, e.target.value)} data-testid={`${testid}-${i}`} />
          {!readOnly && list.length > 1 && (
            <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500 shrink-0"
              onClick={() => onChange(list.filter((_, idx) => idx !== i))}
              data-testid={`${testid}-del-${i}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      ))}
      {!readOnly && (
        <Button type="button" variant="outline" size="sm" className="ml-7"
          onClick={() => onChange([...list, ''])} data-testid={`${testid}-add`}>
          <Plus className="w-3.5 h-3.5 mr-1" /> Add {label.toLowerCase()}
        </Button>
      )}
    </div>
  );
}

export default function StaffAppraisalForm({ form, open, onClose, onDone, readOnly = false }) {
  const [partA, setPartA] = useState(form?.part_a || {});
  const [partB, setPartB] = useState(form?.part_b || {});
  const [busy, setBusy] = useState(false);
  const [formKey, setFormKey] = useState(form?.form_id);

  if (form && formKey !== form.form_id) {
    setFormKey(form.form_id);
    setPartA(form.part_a || {});
    setPartB(form.part_b || {});
  }
  if (!form) return null;

  const a = (k, v) => setPartA(p => ({ ...p, [k]: v }));

  const admissions = partB.admissions && partB.admissions.length
    ? partB.admissions
    : ADMISSION_ROWS.map(d => ({ year: partB.admission_year || '2025-26', description: d, provisional: '', actual: '' }));

  const save = async (submit) => {
    setBusy(true);
    try {
      const url = `${API}/api/faculty-appraisal/forms/${form.form_id}/${submit ? 'submit' : 'save'}`;
      const res = await fetch(url, {
        method: 'PUT', headers: h(), credentials: 'include',
        body: JSON.stringify({ part_a: partA, part_b: { ...partB, admissions } }),
      });
      const d = await safeJson(res);
      if (!res.ok) throw new Error(d.detail || 'Failed');
      toast.success(submit ? `Submitted — next: ${d.next_stage}` : 'Draft saved');
      onDone?.();
      if (submit) onClose();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent
        className="flex flex-col gap-0 p-0 w-full max-w-none h-[100dvh] max-h-[100dvh] rounded-none border-0
                   sm:max-w-none sm:w-screen sm:h-[100dvh] sm:max-h-[100dvh] sm:top-0 sm:left-0
                   sm:translate-x-0 sm:translate-y-0 sm:rounded-none sm:p-0"
        data-testid="staff-appraisal-form"
      >
        <DialogHeader className="shrink-0 border-b border-slate-200 dark:border-slate-700 px-5 py-4 pr-14 text-left space-y-1">
          <DialogTitle>Performance Appraisal for Faculty — A.Y. {form.academic_year}</DialogTitle>
          <DialogDescription>
            Part I (A) &amp; Part I (B) — to be filled by faculty.{' '}
            {readOnly ? 'View only.' : 'Every section accepts as many entries as you need — use “Add Row” / “Add another entry”. Save as draft any time.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4" data-testid="staff-appraisal-body">
          <div className="mx-auto max-w-6xl">
            {form.status === 'returned' && form.return_note && (
              <div className="flex gap-2 rounded-md bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 p-3 text-sm" data-testid="return-note">
                <AlertTriangle className="w-4 h-4 text-orange-600 shrink-0 mt-0.5" />
                <div><b>Returned for correction:</b> {form.return_note}</div>
              </div>
            )}

            <SectionTitle>Part I (A) — Faculty Details</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {PROFILE_FIELDS.map(f => (
                <div key={f.key} className="space-y-1">
                  <Label className="text-xs">{f.label}</Label>
                  <Input type={f.type || 'text'} value={partA[f.key] || ''} disabled={readOnly}
                    onChange={e => a(f.key, e.target.value)} data-testid={`pa-${f.key}`} />
                </div>
              ))}
            </div>

            <SectionTitle>Academic Performance — Theory Courses</SectionTitle>
            <RowTable cols={COURSE_COLS} rows={partA.theory_courses} readOnly={readOnly}
              onChange={v => a('theory_courses', v)} testid="theory-courses" />

            <SectionTitle>Academic Performance — Laboratory Courses</SectionTitle>
            <RowTable cols={COURSE_COLS} rows={partA.lab_courses} readOnly={readOnly}
              onChange={v => a('lab_courses', v)} testid="lab-courses" />

            <SectionTitle>Qualification, Shortfall &amp; Development</SectionTitle>
            {TEXT_BLOCKS.slice(0, 4).map(t => (
              <MultiText key={t.key} label={t.label} hint={t.hint} readOnly={readOnly}
                value={partA[t.key]} onChange={v => a(t.key, v)} testid={`pa-${t.key}`} />
            ))}

            <SectionTitle>Performance as Faculty Guardian</SectionTitle>
            <p className="text-xs text-slate-400 mb-2">Add one row per batch / class you guard.</p>
            <RowTable cols={GUARDIAN_FIELDS} rows={asRows(partA.faculty_guardian, GUARDIAN_FIELDS)}
              readOnly={readOnly} onChange={v => a('faculty_guardian', v)} testid="faculty-guardian" />

            <SectionTitle>Publications — Books</SectionTitle>
            <RowTable cols={BOOK_COLS} rows={partA.books} readOnly={readOnly} onChange={v => a('books', v)} testid="books" />

            <SectionTitle>Publications — Research Papers</SectionTitle>
            <RowTable cols={PAPER_COLS} rows={partA.papers} readOnly={readOnly} onChange={v => a('papers', v)} testid="papers" />
            <div className="space-y-1 mt-3">
              <Label className="text-xs">Google Scholar Profile Link</Label>
              <Input value={partA.google_scholar_link || ''} disabled={readOnly}
                onChange={e => a('google_scholar_link', e.target.value)} data-testid="pa-google_scholar_link" />
            </div>

            <SectionTitle>Student Feedback</SectionTitle>
            <RowTable cols={FEEDBACK_COLS} rows={partA.student_feedback} readOnly={readOnly}
              onChange={v => a('student_feedback', v)} testid="student-feedback" />

            <SectionTitle>Contributions &amp; Professional Development</SectionTitle>
            {TEXT_BLOCKS.slice(4).map(t => (
              <MultiText key={t.key} label={t.label} hint={t.hint} readOnly={readOnly}
                value={partA[t.key]} onChange={v => a(t.key, v)} testid={`pa-${t.key}`} />
            ))}

            <SectionTitle>Your Strengths and Weaknesses</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <MultiLine label="Strength" value={partA.strengths} readOnly={readOnly}
                onChange={v => a('strengths', v)} testid="pa-strengths" />
              <MultiLine label="Weakness" value={partA.weaknesses} readOnly={readOnly}
                onChange={v => a('weaknesses', v)} testid="pa-weaknesses" />
            </div>

            <SectionTitle>Part I (B) — Records of Admission &amp; Revenue</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Name of Head of Department</Label>
                <Input value={partB.hod_name || ''} disabled={readOnly}
                  onChange={e => setPartB(p => ({ ...p, hod_name: e.target.value }))} data-testid="pb-hod_name" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Admission Year</Label>
                <Input value={partB.admission_year || '2025-26'} disabled={readOnly}
                  onChange={e => setPartB(p => ({ ...p, admission_year: e.target.value }))} data-testid="pb-admission_year" />
              </div>
            </div>
            <div className="mt-4">
              <Label className="text-xs uppercase font-semibold">Records of Admission</Label>
              <RowTable
                cols={[{ key: 'description', label: 'Description' }, { key: 'provisional', label: 'Provisional Admission Count' }, { key: 'actual', label: 'Actual Admitted Student Count' }]}
                rows={admissions} readOnly={readOnly}
                onChange={v => setPartB(p => ({ ...p, admissions: v }))} testid="admissions" />
            </div>
            <div className="mt-4 pb-4">
              <Label className="text-xs uppercase font-semibold">Revenue Generated for Department / Institute</Label>
              <RowTable
                cols={[{ key: 'sem', label: 'Sem' }, { key: 'description', label: 'Description' }, { key: 'odd', label: 'Revenue Odd Sem (Rs.)' }, { key: 'even', label: 'Revenue Even Sem (Rs.)' }]}
                rows={partB.revenue} readOnly={readOnly}
                onChange={v => setPartB(p => ({ ...p, revenue: v }))} testid="revenue" />
            </div>
          </div>
        </div>

        <div className="shrink-0 flex justify-end gap-2 border-t border-slate-200 dark:border-slate-700 px-5 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] bg-background">
          <Button variant="outline" onClick={onClose} data-testid="fa-form-close">Close</Button>
          {!readOnly && (
            <>
              <Button variant="secondary" onClick={() => save(false)} disabled={busy} data-testid="fa-save-draft">
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Save Draft
              </Button>
              <Button onClick={() => save(true)} disabled={busy} data-testid="fa-submit">
                <Send className="w-4 h-4 mr-1" /> Submit for Evaluation
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
